import React, { useState, useEffect } from "react";
// form components
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { isEpochDate } from "src/utils/index";
import { SearchDataType, ISavedFilter, IFilterOption, IUser } from "@vmosolutions/oc-interfaces";

export interface ISearchStatus {
  active: boolean;
  message?: string;
  code?: number;
  lastSearched?: Date;
}

export interface IEventFilter {
  dataType?: SearchDataType;
  dataInput?: any; // incoming event dataset
  dataOutput: (data: any) => any; // returns filtered dataset
  allowMultiFilter?: boolean;
  parentElement?: any;
  hideLabel?: boolean;
  searchStatus?: (status: ISearchStatus) => void;
  noFilter?: boolean;
  showDateRangeRRule?: boolean;
  inHeader?: boolean;
  setSearchFilters?: React.Dispatch<React.SetStateAction<IFilterOption[]>>;
  applySavedFilter?: ISavedFilter;
}

interface IFilterWithDate {
  className?: string;
  custom?: any;
  dateTimeRange?: {
    minTime: string;
    maxTime: string;
    daysOfWeek: IDaysOfWeekOption[];
  };
  input?: any;
  label?: string;
  meta?: any;
  name?: string;
  onBlur?: (event: any) => void;
  onChange?: (event: any) => void;
  type?: string;
  showDateRangeRRule?: boolean;
}

export interface IDaysOfWeekOption {
  label: string;
  value: number;
  rrule: string;
}

// EventHelper converts array of selected daysofweek to it's rrule for easier parsing on the backend.
const daysOfWeekOptions = [
  { label: "Sunday", value: 0, rrule: "SU" },
  { label: "Monday", value: 1, rrule: "MO" },
  { label: "Tuesday", value: 2, rrule: "TU" },
  { label: "Wednesday", value: 3, rrule: "WE" },
  { label: "Thursday", value: 4, rrule: "TH" },
  { label: "Friday", value: 5, rrule: "FR" },
  { label: "Saturday", value: 6, rrule: "SA" },
];

export const defaultSearchStatus = { active: false, message: "" };

/**
 * Return selected filter options based on searchValue in associated filter.
 * @param options - options for specific field
 * @param searchFilterValue - selected filter searchValue array
 * @param evalKey - key to identify if option is selected (normally _id);
 * @returns
 */
export const returnSelectedOptions = (options: any[], searchFilterValue: any[], evalKey: string): any[] => {
  if (searchFilterValue?.length === 0 || options?.length === 0) {
    return [];
  } else if (searchFilterValue?.length > 0 && options?.length > 0) {
    return options.filter((option: any) => searchFilterValue.includes(option[evalKey]));
  }
  return [];
};

/**
 * Function that returns a DateTimeInput for the user to choose a date and time range
 * to filter through the flights in the table
 * @param props -
 */
export const FilterWithDateInput = (props: IFilterWithDate): JSX.Element => {
  const { className, dateTimeRange, onBlur, onChange, name, showDateRangeRRule } = props;
  const { minTime, maxTime, daysOfWeek } = dateTimeRange ?? {};

  const [dowValue, setDowValue] = useState<IDaysOfWeekOption[]>([]);
  const [dowPlaceholder, setDowPlaceholder] = useState<string>("");
  useEffect(() => {
    if (!showDateRangeRRule) {
      return;
    }
    // Convert number array to selection array used in input.
    // If value is null, assume full day selection (show all checked);
    // Note: Checkbox values have to be empty when Select is mounted (hence the useEffect)
    const daysOfWeekValue = daysOfWeek ?? daysOfWeekOptions;
    const placeholder = daysOfWeekValue.map((option) => option.rrule).toString();
    setDowValue(daysOfWeekValue);
    setDowPlaceholder(placeholder);
  }, [daysOfWeek, showDateRangeRRule]);

  const handleMinDateChange = (minTime: string | number): void => {
    const newDateTimeRange = { ...dateTimeRange, minTime };
    onChange(newDateTimeRange);
  };

  const handleMaxDateChange = (maxTime: string | number): void => {
    const newDateTimeRange = { ...dateTimeRange, maxTime };
    onChange(newDateTimeRange);
  };

  const handleDayPatternChange = (values: IDaysOfWeekOption[]): void => {
    const newDateTimeRange = { ...dateTimeRange, daysOfWeek: values };
    onChange(newDateTimeRange);
  };

  return (
    <div className={className}>
      <DateTimeField
        className="from_date"
        label={`${name} From`}
        name="min_time"
        value={minTime}
        onChange={handleMinDateChange}
        onBlur={onBlur}
        maxDate={maxTime}
        isEpoch={isEpochDate(minTime)}
      />

      {showDateRangeRRule && (
        <SelectField
          className="daysofweek_selection"
          label="On Days Of Week"
          name="days_of_week"
          value={dowValue}
          onChange={handleDayPatternChange}
          options={daysOfWeekOptions}
          isClearable={false}
          menuPosition="fixed"
          maxMenuHeight={400}
          openMenuOnFocus={false}
          closeMenuOnSelect={false}
          placeholder={dowPlaceholder}
          isMulti={true}
          multiMode="checkbox"
          hideSelectedValues={true}
        />
      )}

      <DateTimeField
        className="to_date"
        label={"Until"}
        name="min_time"
        value={maxTime}
        onChange={handleMaxDateChange}
        onBlur={onBlur}
        minDate={minTime}
        isEpoch={isEpochDate(maxTime)}
      />
    </div>
  );
};

export const sortUserOptions = (appUsers: IUser[], currentUserId?: string): IUser[] => {
  //regular sort
  const sortedUsers = appUsers.sort((a, b) => {
    return a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase());
  });
  //add me to the start of the array
  const curUserIndex = sortedUsers.findIndex((user: IUser) => user._id === currentUserId);
  if (curUserIndex > -1) {
    const curUser = sortedUsers[curUserIndex];
    sortedUsers.splice(curUserIndex, 1); //remove cur user
    sortedUsers.unshift(curUser); //add them back in at the start of the array
  }
  return sortedUsers;
};
